type Pitch = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';
type Alteration = '#' | 'b';
type Octave = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
type PitchClassName = `${Pitch}` | `${Pitch}${Alteration}`;
type Name =
  | PitchClassName
  | `${Pitch}${Octave}`
  | `${Pitch}${Alteration}${Octave}`;

type ToNameOptions = {
  preferSharps?: boolean;
  withOctave?: boolean;
};

export type MidiNote = number; // 0-127

export class NoteRange {
  constructor(public readonly from: Note, public readonly to: Note) {
    if (from.toMidi() > to.toMidi()) throw new Error('Invalid range');
  }

  contains(note: Note): boolean {
    const n = note.toMidi();

    return n >= this.from.toMidi() && n <= this.to.toMidi();
  }

  *notes(): IterableIterator<Note> {
    for (let m = this.from.toMidi(); m <= this.to.toMidi(); m++) {
      yield Note.fromMidi(m);
    }
  }

  size(): number {
    return this.to.toMidi() - this.from.toMidi() + 1;
  }
}

export class Note {
  private constructor(private readonly midi: number) {}

  static fromMidi(midi: number): Note {
    if (!Number.isInteger(midi) || midi < 0 || midi > 127) {
      throw new Error('Invalid MIDI note');
    }
    return new Note(midi);
  }

  static fromName(name: Name): Note {
    return new Note(noteNameToMidi(name));
  }

  toMidi(): number {
    return this.midi;
  }

  toName({ preferSharps = true, withOctave = true }: ToNameOptions = {}): Name {
    return midiToNoteName(this.midi, preferSharps, withOctave);
  }

  equals(other: Note): boolean {
    return this.midi === other.midi;
  }

  transpose(semitones: number): Note {
    return Note.fromMidi(this.midi + semitones);
  }
}

const PITCH_TO_SEMITONE: Record<Pitch, number> = {
  C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11,
};

function noteNameToMidi(name: Name): number {
  const m = name.trim().toUpperCase().match(/^([A-G])([#B]?)(-?\d+)$/);
  if (!m) throw new Error(`Invalid note name: ${name}`);

  const pitch = m[1] as Pitch;
  const alt = m[2];
  const octave = Number(m[3]);

  let semitone = PITCH_TO_SEMITONE[pitch];
  if (alt === '#') semitone += 1;
  if (alt === 'B') semitone -= 1;

  const midi = (octave + 1) * 12 + semitone;

  if (!Number.isInteger(midi) || midi < 0 || midi > 127) {
    throw new Error(`Out of MIDI range: ${name} -> ${midi}`);
  }

  return midi;
}

function midiToNoteName(midi: number, preferSharps = true, withOctave = true): Name {
  if (!Number.isInteger(midi) || midi < 0 || midi > 127) throw new Error('Invalid MIDI note');

  const octave = Math.floor(midi / 12) - 1 as Octave;
  const pc = midi % 12;

  const sharpNames = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
  const flatNames  = ['C','Db','D','Eb','E','F','Gb','G','Ab','A','Bb','B'];
  const name = (preferSharps ? sharpNames : flatNames)[pc];

  return (withOctave ? `${name}${octave}` : name) as Name;
}
